import { APP_TITLE, DETAIL_SEPARATOR, TITLE_SEPARATOR } from './constants';

/**
 * Title metadata declared on a route definition via `handle`.
 *
 * Static only — a route knows it is "a source page", never *which* source. The
 * entity name is published at render time by the page itself; see ./entityName.
 */
export type RouteTitleHandle = {
  /** The section the route belongs to, e.g. `Sources`. */
  section?: string;
  /** An action performed on the entity, e.g. `Connect Destination`. */
  action?: string;
  /**
   * A grouping between the entity and its section, e.g. `Groups` in
   * `Eng - Groups - Access Management`. Unlike an action it sits AFTER the
   * entity name, because it classifies the entity rather than describing
   * something being done to it.
   */
  subsection?: string;
};

/**
 * Resolves the handle for the current match chain.
 *
 * Walked root to leaf so a child route inherits its parent's section — a page
 * nested under `/profiles/:id` is still a Profiles page — while anything the
 * child declares itself wins.
 */
export const resolveRouteTitleHandle = (
  handles: (RouteTitleHandle | undefined)[],
): RouteTitleHandle =>
  handles.reduce<RouteTitleHandle>(
    (resolved, handle) => ({
      section: handle?.section ?? resolved.section,
      action: handle?.action ?? resolved.action,
      subsection: handle?.subsection ?? resolved.subsection,
    }),
    {},
  );

/**
 * Composes the document title.
 *
 *   Section pages   →  `Section | RudderStack`
 *   Detail pages    →  `Entity Name - Section | RudderStack`
 *   Action pages    →  `Action - Entity Name - Section | RudderStack`
 *   Sub-sectioned   →  `Entity Name - Subsection - Section | RudderStack`
 *
 * The section is never dropped. When the entity name has not loaded yet an
 * action page reads `Action - Section`, never the bare action — otherwise three
 * unrelated live-events routes all render "Live Events" on a cold load, and
 * `/sources/:id/connect` claims to be a destination page while the user is
 * looking at a source.
 */
export const buildDocumentTitle = ({
  section,
  action,
  subsection,
  entityName,
}: RouteTitleHandle & { entityName?: string }): string => {
  const titleParts = [action, entityName?.trim() || undefined, subsection, section].filter(Boolean);

  if (titleParts.length === 0) {
    return APP_TITLE;
  }

  return `${titleParts.join(DETAIL_SEPARATOR)}${TITLE_SEPARATOR}${APP_TITLE}`;
};
